/*
 *  -----------------------------------------------------------------------
 *    <copyright file="unassign-crf-confirmation-dialog.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import React from 'react';
import { UnassignAssessmentConfirmationDialogProps } from './unassign-assessment-confirmation-dialog.interface';
import { useTranslation } from 'hooks';
import { SAVE_STUDY_VISIT_SCHEDULE_VISIT } from 'queries/study-visit-schedules';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';

const UnassignAssessmentConfirmanionDialog = ({
    studyId,
    currentScheduleId,
    currentAssessment,
    currentVisit,
    close,
}: UnassignAssessmentConfirmationDialogProps) => {
    const { t } = useTranslation();
    const [saveVisit] = useMutation(SAVE_STUDY_VISIT_SCHEDULE_VISIT);

    const onClose = (action?: string) => {
        close(action);
    };

    const unassignSubmit = () => {
        const remainingIds = currentVisit.eventIds?.filter((itemId) => itemId !== currentAssessment.id);

        const submitData = {
            studyId,
            visitScheduleId: currentScheduleId,
            visit: {
                id: currentVisit.id,
                active: currentVisit.active,
                sequenceNumber: currentVisit.sequenceNumber,
                type: currentVisit.type,
                visitId: currentVisit.visitId,
                visitName: currentVisit.visitName,
                leadVisitId: currentVisit.leadVisitId,
                leadPeriod: currentVisit.leadPeriod,
                leadUnit: currentVisit.leadUnit,
                minWinPeriod: currentVisit.minWinPeriod,
                maxWinPeriod: currentVisit.maxWinPeriod,
                minWinUnit: currentVisit.minWinUnit,
                minOccurrences: currentVisit.minOccurrences,
                forecastEndDateField: currentVisit.forecastEndDateField,
                isEndDateEarliest: currentVisit.isEndDateEarliest,
                eventIds: remainingIds,
            },
        };

        saveVisit({
            variables: {
                ...submitData,
            },
        })
            .then(() => {
                onClose('unassign');
            })
            .catch(() => {
                onClose('error');
            });
    };

    return (
        <CustomDialog
            onClose={() => onClose()}
            fullWidth
            maxWidth="md"
            scroll="paper"
            open
            className="custom-width-popup"
        >
            <DialogTitle id="unassign-assessment-visit-modal-title">
                {t('admin:unassignAssessment')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => onClose()}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="unassign-crf-dialog-content">
                <div className="delete-visit-text">{t('admin:unassignVisitAssessmentQuestion')}</div>
                {currentVisit && (
                    <div className="content-text">
                        <span className="title">{t('subject:visitName')}:</span>
                        {currentVisit.visitName}
                    </div>
                )}
                {currentAssessment && (
                    <div className="content-text">
                        <span className="title">{t('admin:assessmentName')}:</span>
                        {currentAssessment.name}
                    </div>
                )}
                <DialogActions className="add-treatment-dialog-content-actions flex-end">
                    <Button id="confirm-unassign-assessment-btn" text={t('common:unassign')} onClick={unassignSubmit} />
                </DialogActions>
            </DialogContent>
        </CustomDialog>
    );
};

export default UnassignAssessmentConfirmanionDialog;
